import React from 'react';
import { ProductTile, ProductTileSkeleton } from '@hpi-sox/hpstellar-core';
import loadable from '@loadable/component';

import ProductJsonld from '../product-json-ld';
import AccessibleElement from '../../../ui/components/accessible-element';
import AboveTheFoldOnlyServerRender from '../../../shared/components/above-the-fold-only-server-render';
import ConditionalWrapper from '../../../shared/components/conditional-wrapper';
import { Helpers } from '../../../core/src/helpers';
import useDeviceInfo from '../../../hooks/useDeviceInfo';
import useProductPrice from '../../../hooks/useProductPrice';

const VisIDProductTile = loadable(() => Helpers.retryFunc(() => import('../visId/product-tile')));
const VisIDProductTileHorizontal = loadable(() => Helpers.retryFunc(() => import('../visId/product-tile-horizontal')));
const OrionProductTile = loadable(() => Helpers.retryFunc(() => import('../orion/product-tile')));

const parseTranslations = translations => {
    return translations
        ? {
              ...(translations || {}),
              ...(translations.buttons
                  ? {
                        buttons: {
                            ...translations.buttons,
                            ...(translations.buttons.compareBtnText
                                ? {
                                      compareBtnText: (
                                          <h6 className="compareLabel">{translations.buttons.compareBtnText}</h6>
                                      ),
                                  }
                                : {}),
                        },
                    }
                  : {}),
          }
        : {};
};

/**
 * Wrapper component that renders children with ProductJsonLd
 * @param {React.ReactNode} children - Child components to render
 * @param {Object} product - Product data
 * @param {Object} price - Price data
 */
export const ProductJsonLdWrapper = ({ children, product }) => {
    const { isBot } = useDeviceInfo(['isBot']);
    const prices = useProductPrice([product]);
    const price = (prices || {})[product?.sku];

    return (
        <>
            {children}
            {product && <ProductJsonld product={{ ...product }} price={price} enableImageDetails={isBot} />}
        </>
    );
};

/**
 * Displays ProductTile stellar component and associated jsonld.
 * Use this component in cases where the ProductTile needs to be rendered along with the jsonlds.
 *
 */
export default ({
    price,
    product,
    translations,
    lazyLoadSettings,
    visId,
    containerProps,
    horizontal,
    forceShowPlaceholder,
    ProductTileComponentProp,
    orionEnabled,
    ...rest
}) => {
    const ProductTileComponent = ProductTileComponentProp
        ? ProductTileComponentProp
        : orionEnabled
          ? OrionProductTile
          : visId
            ? horizontal
                ? VisIDProductTileHorizontal
                : VisIDProductTile
            : ProductTile;
    const { sku } = product || {};
    const accessibilityProps = product && { 'data-sku': sku };
    const { isBot } = useDeviceInfo(['isBot']);
    return (
        <AccessibleElement element="div" key={sku} clickTarget {...accessibilityProps} {...(containerProps || {})}>
            <ConditionalWrapper
                condition={!!lazyLoadSettings || forceShowPlaceholder}
                wrapper={children => (
                    <AboveTheFoldOnlyServerRender
                        placeholder={<ProductTileSkeleton style={{ overflow: 'hidden' }} />}
                        forceShowPlaceholder={forceShowPlaceholder}
                        {...lazyLoadSettings}
                    >
                        {children}
                    </AboveTheFoldOnlyServerRender>
                )}
            >
                {/* TODO: probably need to ask gfe for a ref prop on product tile directly */}
                <div className="tileRef" ref={rest.productRef}>
                    <ProductTileComponent
                        {...rest}
                        translations={parseTranslations(translations)}
                        price={price}
                        product={product}
                        ratingSpaceReserved={true}
                    />
                </div>
            </ConditionalWrapper>
            {!forceShowPlaceholder && product && (
                <ProductJsonld product={{ ...product }} price={price} enableImageDetails={isBot} />
            )}
        </AccessibleElement>
    );
};
